import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Slide } from "../components/HeroSlide/HeroSlide";
import { getPublishedEpisodes, getUpcomingEpisodes } from "shared/data/episodeRepository";

interface SlideProps {
  slide: Slide
  badgeLabel: string
}

export const useGenerateSlides = (): SlideProps[] => {
  const { t } = useTranslation('home');
  const { upcomingEpisodes, latestEpisode, bonusEpisode } = useMemo(() => {
    const published = getPublishedEpisodes();
    const upcoming = getUpcomingEpisodes();

    const firstPublished = published[0];
    const firstIsBonus = Boolean(firstPublished?.bonus);

    return {
      upcomingEpisodes: upcoming,
      latestEpisode: firstIsBonus ? published[1] : firstPublished,
      bonusEpisode: firstIsBonus ? firstPublished : undefined,
    };
  }, []);

  const latestEpisodeSlide = useMemo(() => {
    if (!latestEpisode) return []

    return [{ 
      slide: {
        ...latestEpisode,
        primaryCta: {
          label: t('hero.listenToEpisode'),
          to: `/episodes/${latestEpisode.number}`
        },
        secondaryCta: {
          label: t('hero.viewShowNotes'),
          to: `/episodes/${latestEpisode.number}#show-notes`,
        }
      },
      badgeLabel: t('hero.badgeLatest'),
    }]
  },[latestEpisode, t])

  const bonusEpisodeSlide = useMemo(() => {
    if (!bonusEpisode) return []

    return [{
      slide: {
        ...bonusEpisode,
        primaryCta: {
          label: t('hero.listenToEpisode'),
          to: `/episodes/${bonusEpisode.number}`
        },
        secondaryCta: {
          label: t('hero.viewShowNotes'),
          to: `/episodes/${bonusEpisode.number}#show-notes`,
        }
      },
      badgeLabel: t('hero.badgeBonus'),
    }]
  },[bonusEpisode, t])

  const upcomingEpisodeSlides = useMemo(() => {
    return upcomingEpisodes.map((ep) => (
      {
        slide: ep,
        badgeLabel: t('hero.badgeUpcoming')
      }
    ))
  }, [upcomingEpisodes, t]);

  return [
    ...bonusEpisodeSlide,
    ...latestEpisodeSlide,
    ...upcomingEpisodeSlides
  ]
}
